import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { sendInquiry, ValidationError } from '../lib/api'
import type { ValidationErrors } from '../lib/api'
import { Turnstile } from '../components/Turnstile'
import type { TurnstileHandle } from '../components/Turnstile'

type Status = 'idle' | 'sending' | 'sent' | 'failed'

const EMAIL = 'haydn@loonworkssoftware.com'

export function Contact() {
  const [status, setStatus] = useState<Status>('idle')
  const [errors, setErrors] = useState<ValidationErrors>({})
  const turnstile = useRef<TurnstileHandle>(null)
  const [form, setForm] = useState({
    name: '',
    email: '',
    business: '',
    message: '',
    website: '',
    turnstile_token: '',
  })

  const set = (field: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [field]: value }))

  async function submit(e: FormEvent) {
    e.preventDefault()
    setStatus('sending')
    setErrors({})
    try {
      await sendInquiry(form)
      setStatus('sent')
    } catch (err) {
      // Any submit that reached the server spent the Turnstile token; get a
      // fresh one before the retry.
      turnstile.current?.reset()
      set('turnstile_token')('')
      if (err instanceof ValidationError) {
        setErrors(err.errors)
        setStatus('idle')
      } else {
        setStatus('failed')
      }
    }
  }

  return (
    <section className="section">
      <div className="wrap">
        <p className="eyebrow">Contact</p>
        <h1>Tell me about your business.</h1>
        <p className="lede">
          A few sentences is plenty: what you do, what your current site is,
          and what's not working. I read every inquiry.
        </p>
        <p>
          You'll get a reply from a person, not an automated sequence. If it
          looks like a fit, the next step is a conversation, then a written
          plan with a real price. Nothing is owed until you say go.
        </p>
        <p>
          Prefer email? Write to <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
        </p>

        {status === 'sent' ? (
          <div className="form-sent">
            <h3>Got it. Thanks.</h3>
            <p style={{ margin: 0 }}>
              Your note is in. You'll hear back at {form.email}.
            </p>
          </div>
        ) : (
          <form className="inquiry-form" onSubmit={submit} noValidate>
            <div className="field">
              <label htmlFor="inq-name">Your name</label>
              <input
                id="inq-name"
                type="text"
                autoComplete="name"
                required
                value={form.name}
                onChange={(e) => set('name')(e.target.value)}
              />
              {errors.name && <p className="field-error">{errors.name[0]}</p>}
            </div>

            <div className="field">
              <label htmlFor="inq-email">Email</label>
              <input
                id="inq-email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(e) => set('email')(e.target.value)}
              />
              {errors.email && <p className="field-error">{errors.email[0]}</p>}
            </div>

            <div className="field">
              <label htmlFor="inq-business">
                Business name <span className="hint">(optional)</span>
              </label>
              <input
                id="inq-business"
                type="text"
                autoComplete="organization"
                value={form.business}
                onChange={(e) => set('business')(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="inq-message">What's going on?</label>
              <textarea
                id="inq-message"
                rows={6}
                required
                value={form.message}
                onChange={(e) => set('message')(e.target.value)}
              />
              {errors.message && (
                <p className="field-error">{errors.message[0]}</p>
              )}
            </div>

            <div className="trap" aria-hidden="true">
              <label htmlFor="inq-website">Leave this field empty</label>
              <input
                id="inq-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => set('website')(e.target.value)}
              />
            </div>

            <div className="field">
              <Turnstile ref={turnstile} onToken={set('turnstile_token')} />
              {errors.turnstile_token && (
                <p className="field-error">{errors.turnstile_token[0]}</p>
              )}
            </div>

            {status === 'failed' && (
              <p className="form-status form-status--error">
                The note didn't send. Something went wrong on this end. Try
                again in a minute, or email{' '}
                <a href={`mailto:${EMAIL}`}>{EMAIL}</a> directly.
              </p>
            )}

            <div>
              <button
                type="submit"
                className="button button--primary"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Send inquiry'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  )
}
